import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MapPin, Building2, Home, Warehouse, Loader2, Hash } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const nigerianStates = [
  "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno",
  "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "FCT", "Gombe",
  "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara",
  "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau",
  "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara"
];

const AddAssetPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    asset_type: "",
    state: "",
    lga: "",
    address: "",
    gps_coordinates: "",
    ownership_type: "individual",
    estimated_value: "",
    description: "",
    plot_number: "",
    unit_number: "",
    block_number: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        ...formData,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        plot_number: formData.plot_number || null,
        unit_number: formData.unit_number || null,
        block_number: formData.block_number || null
      };
      
      const response = await axios.post(`${API}/assets`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Asset added successfully!");
      navigate(`/assets/${response.data.asset_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add asset");
    } finally {
      setLoading(false);
    }
  };

  const assetTypes = [
    { value: "land", label: "Land", icon: MapPin },
    { value: "residential", label: "Residential", icon: Home },
    { value: "commercial", label: "Commercial", icon: Warehouse }
  ];

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="add-asset-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/assets">
            <Button variant="ghost" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Add New Asset
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Asset Type Selection */}
              <div className="space-y-3">
                <Label className="text-slate-300">Asset Type</Label>
                <div className="grid grid-cols-3 gap-4">
                  {assetTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, asset_type: type.value })}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.asset_type === type.value
                            ? 'border-primary bg-primary/10'
                            : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                        }`}
                        data-testid={`asset-type-${type.value}`}
                      >
                        <Icon className={`w-6 h-6 mx-auto mb-2 ${
                          formData.asset_type === type.value ? 'text-primary' : 'text-slate-400'
                        }`} />
                        <p className={`text-sm font-medium ${
                          formData.asset_type === type.value ? 'text-white' : 'text-slate-300'
                        }`}>
                          {type.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Asset Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-300">Asset Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="e.g., Victoria Island Plot A"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-default"
                  required
                  data-testid="asset-name-input"
                />
              </div>

              {/* Property Identifier - depends on asset type */}
              {formData.asset_type === "land" && (
                <div className="space-y-2">
                  <Label htmlFor="plot_number" className="text-slate-300 flex items-center gap-2">
                    <Hash className="w-4 h-4 text-primary" />
                    Plot Number / AGIS Number (Optional)
                  </Label>
                  <Input
                    id="plot_number"
                    name="plot_number"
                    placeholder="e.g., PLT-2045 or AGIS/FCT/0001234"
                    value={formData.plot_number}
                    onChange={handleChange}
                    className="input-default"
                    data-testid="plot-number-input"
                  />
                  <p className="text-xs text-slate-500">
                    Unique plot identifier. Must be unique within the same state.
                  </p>
                </div>
              )}
              {formData.asset_type === "residential" && (
                <div className="space-y-2">
                  <Label htmlFor="unit_number" className="text-slate-300 flex items-center gap-2">
                    <Hash className="w-4 h-4 text-primary" />
                    Unit Number (Optional)
                  </Label>
                  <Input
                    id="unit_number"
                    name="unit_number"
                    placeholder="e.g., UNIT-A12 or Flat 3B"
                    value={formData.unit_number}
                    onChange={handleChange}
                    className="input-default"
                    data-testid="unit-number-input"
                  />
                  <p className="text-xs text-slate-500">
                    Unique flat/unit identifier. Must be unique within the same state.
                  </p>
                </div>
              )}
              {formData.asset_type === "commercial" && (
                <div className="space-y-2">
                  <Label htmlFor="block_number" className="text-slate-300 flex items-center gap-2">
                    <Hash className="w-4 h-4 text-primary" />
                    Block Number (Optional)
                  </Label>
                  <Input
                    id="block_number"
                    name="block_number"
                    placeholder="e.g., BLOCK-7 or BLK-A"
                    value={formData.block_number}
                    onChange={handleChange}
                    className="input-default"
                    data-testid="block-number-input"
                  />
                  <p className="text-xs text-slate-500">
                    Unique block identifier. Must be unique within the same state.
                  </p>
                </div>
              )}

              {/* Location */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state" className="text-slate-300">State</Label>
                  <Select 
                    value={formData.state} 
                    onValueChange={(value) => setFormData({ ...formData, state: value })}
                  >
                    <SelectTrigger className="input-default" data-testid="state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {nigerianStates.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="lga" className="text-slate-300">LGA</Label>
                  <Input
                    id="lga"
                    name="lga"
                    placeholder="Local Government Area"
                    value={formData.lga}
                    onChange={handleChange}
                    className="input-default"
                    required
                    data-testid="lga-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address" className="text-slate-300">Full Address</Label>
                <Input
                  id="address"
                  name="address"
                  placeholder="Street address"
                  value={formData.address}
                  onChange={handleChange}
                  className="input-default"
                  required
                  data-testid="address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gps_coordinates" className="text-slate-300">GPS Coordinates (Optional)</Label>
                <Input
                  id="gps_coordinates"
                  name="gps_coordinates"
                  placeholder="e.g., 6.4541° N, 3.3947° E"
                  value={formData.gps_coordinates}
                  onChange={handleChange}
                  className="input-default"
                  data-testid="gps-input"
                />
              </div>

              {/* Ownership */}
              <div className="space-y-2">
                <Label className="text-slate-300">Ownership Type</Label>
                <Select 
                  value={formData.ownership_type} 
                  onValueChange={(value) => setFormData({ ...formData, ownership_type: value })}
                >
                  <SelectTrigger className="input-default" data-testid="ownership-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="estate">Estate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Value */}
              <div className="space-y-2">
                <Label htmlFor="estimated_value" className="text-slate-300">Estimated Value (₦) - Optional</Label>
                <Input
                  id="estimated_value"
                  name="estimated_value"
                  type="number"
                  placeholder="e.g., 50000000"
                  value={formData.estimated_value}
                  onChange={handleChange}
                  className="input-default"
                  data-testid="value-input"
                />
                <p className="text-xs text-slate-500">
                  You can add a manual estimate or use AI appraisal later
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-slate-300">Description (Optional)</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Additional details about the property..."
                  value={formData.description}
                  onChange={handleChange}
                  className="input-default min-h-[100px]"
                  data-testid="description-input"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-4">
                <Link to="/assets" className="flex-1">
                  <Button type="button" variant="outline" className="w-full btn-secondary">
                    Cancel
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  className="flex-1 btn-primary"
                  disabled={loading || !formData.name || !formData.asset_type || !formData.state || !formData.lga || !formData.address}
                  data-testid="submit-asset-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add Asset"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AddAssetPage;
